// Shared OpenStreetMap tile layer.
//
// OSM's tile usage policy rejects requests that arrive without a Referer, and
// the proxy sends `Referrer-Policy: strict-origin-when-cross-origin` for the
// admin host, while every other host keeps `same-origin`. Each tile element
// repeats the admin policy so the requirement stays explicit at the OSM
// integration boundary. Cross-origin requests expose only the origin, never a
// path or query.
//
// Admin runs two different Leaflets and they need opposite techniques:
//
//   * static/js/leaflet.js — a 0.7-era build exposed as the global `L`, used by
//     staticmap.js, map.js and overview_map.js. It predates the referrerPolicy
//     option, so the tile element has to be patched in `_createTile`.
//   * the npm `leaflet` 1.9.4 package, imported by the Vue components. It
//     honours a `referrerPolicy` option natively and has no `_createTile` at
//     all — patching it the legacy way would throw.
//
// Pass whichever `L` the calling module uses; the branch is chosen by feature
// detection rather than by version, so this keeps working if either Leaflet is
// upgraded. Every map in admin must build its tiles through here: a plain
// `L.tileLayer(...)` looks fine in review, works in local development, and then
// silently has its tiles rejected by OSM in production.

const TILE_REFERRER_POLICY = 'strict-origin-when-cross-origin';

export const OSM_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

// One subclass per Leaflet namespace, built on first use.
const legacyClasses = new WeakMap();

function legacyTileLayerClass(LeafletNS) {
    if (!legacyClasses.has(LeafletNS)) {
        legacyClasses.set(
            LeafletNS,
            LeafletNS.TileLayer.extend({
                _createTile: function () {
                    const tile = LeafletNS.TileLayer.prototype._createTile.call(this);
                    // Set before Leaflet assigns src, so it affects the request.
                    tile.referrerPolicy = TILE_REFERRER_POLICY;
                    return tile;
                },
            })
        );
    }
    return legacyClasses.get(LeafletNS);
}

export function osmTileLayer(LeafletNS, url = OSM_TILE_URL, options = {}) {
    const opts = { ...options, referrerPolicy: TILE_REFERRER_POLICY };

    if (typeof LeafletNS.TileLayer.prototype._createTile === 'function') {
        const LegacyTileLayer = legacyTileLayerClass(LeafletNS);
        return new LegacyTileLayer(url, opts);
    }

    return LeafletNS.tileLayer(url, opts);
}
